import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import API from '../../utils/ApiConfig';
import Loader from '../common/Loader/Loader';
import { CREATE_BUDGET, GET_ALL_DIMENSIONS, GET_BUDGET_YEARS, GET_GROWTH_RATES } from '../../utils/ApiEndpoints';
import Header from '../Header/Header';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useTranslation } from 'react-i18next';
import { budgetDetails, dimensionIcons } from './Utility';
import ErrorMessage from '../common/ErrorMessage/ErrorMessage';
import ProgressDialog from '../common/ProgressDialog/ProgressDialog';
import { BUDGET_PROGRESS_DESTINATION } from '../../utils/WebSocketDestinations';
import { useUnload } from '../../utils/CustomHooks';

const Onboard = props => {
  const [budgetingDimensions, setBudgetingDimensions] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedGrowthRate, setSelectedGrowthRate] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [budgetYears, setBudgetYears] = useState({});
  const [growthRates, setGrowthRates] = useState([]);

  const handleChange = ({ target: { value } }) => setSelectedGrowthRate(value);

  const getAllDimensions = id => API.get(GET_ALL_DIMENSIONS(id));

  const getBudgetYears = () => API.get(GET_BUDGET_YEARS);

  const getGrowthRates = () => API.get(GET_GROWTH_RATES);

  const [showProgress, setShowProgress] = useState(false);
  const [destroyProgress, setDestroyProgress] = useState(false);
  useUnload(() => setDestroyProgress(true));

  useEffect(() => {
    const { id } = userProfile;
    if (id && id.length) {
      Promise.all([getAllDimensions(id), getBudgetYears(), getGrowthRates()])
        .then(([dimensions, budgetYears, growthRates]) => {
          dimensions.data && setBudgetingDimensions(dimensions.data);
          budgetYears.data && setBudgetYears(budgetYears.data);
          if (growthRates.data && growthRates.data.growthRateList && growthRates.data.growthRateList.length) {
            setGrowthRates(growthRates.data.growthRateList);
            if (growthRates.data.growthRateList.length === 1) {
              setSelectedGrowthRate(growthRates.data.growthRateList[0].growthRateId);
            }
          }
        })
        .catch(err => {
          err && err.response && err.response.data && setErrorMessage(err.response.data.status);
          setShowErrorMessage(true);
        })
        .finally(() => setLoading(false));
    }
  }, [userProfile]);

  useEffect(() => {
    const {
      services: { profileService, CommonService },
    } = props;
    setLoading(true);
    profileService.loadConnectionList(CommonService.auth.getCurrentUser().id).then(() => {
      if (profileService.DATA.connections.length) {
        setUserProfile(profileService.DATA.connections[0]);
      }
    });
  }, []);

  const CardElement = ({ growthRateId, growthRate, description, selectedGrowthRate }) => (
    <Card className={`growth-rate-container ${selectedGrowthRate && selectedGrowthRate == growthRateId ? 'growth-rate-container-active' : ''}`}>
      <div>
        <div className="growth-rate-icon">
          <i className={growthRateId === 'ZERO_GROWTH_RATE' ? 'icon-Zero-Revenue-Growth' : 'fa fa-line-chart'} aria-hidden="true"></i>
        </div>
        <div className="growth-rate-title">{growthRate}</div>
        <div className="growth-rate-desc">{description}</div>
      </div>
    </Card>
  );

  const handleCreateBudget = () => {
    const {
      services: { CommonService },
    } = props;
    setShowProgress(true);
    API.post(CREATE_BUDGET, {
      growthRate: selectedGrowthRate,
    })
      .then(data => {
        setDestroyProgress(true);
        // redirect users to the reports page
        CommonService.redirectToReportsOnboarding();
      })
      .catch(err => {
        err && err.response && err.response.data && setErrorMessage(err.response.data.status);
        setShowErrorMessage(true);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const { t } = useTranslation();

  const tc = (e, data) => (data ? t(`onboard:${e}`, data) : t(`onboard:${e}`));

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const { previousFiscalYear, numOfYears } = budgetYears;

  const { versionName } = budgetDetails;

  return (
    <div className="onboard">
      <ProgressDialog show={showProgress} destroy={destroyProgress} destination={BUDGET_PROGRESS_DESTINATION} />
      {isLoading ? <Loader /> : ''}
      <div className="onboard-top-section">
        <Header headerIcon="icon-centage-primary-logo" />
        <div className="onboard-welcome-card">
          <div className="onboard-welcome-title">{tc('PM_WELCOME_TITLE')}</div>
          <div className="onboard-welcome-desc">
            {tc('PM_WELCOME_DESC1')} <b>{tc('BUDGET')},</b> <b>{tc('FORECAST')},</b> {tc('PM_WELCOME_DESC2')} <b>{tc('REPORTING')}</b>{' '}
            {tc('PM_WELCOME_DESC3')}
          </div>
        </div>
        {Object.keys(budgetYears).length ? (
          <div className="budget-desc">
            {tc('BUDGET_DESC1', { versionName, numOfYears })} <b>{tc('BUDGET_DESC2', { previousFiscalYear })} </b>
            {tc('BUDGET_DESC3', { versionName })}
          </div>
        ) : (
          ''
        )}

        {growthRates.length ? (
          <RadioGroup aria-label="growthRate" name="growthRate" value={selectedGrowthRate} onChange={handleChange} className="growth-rate">
            <TableContainer>
              <Table style={growthRates.length == 1 ? { maxWidth: '500px', margin: 'auto' } : {}}>
                <TableBody>
                  <TableRow>
                    {growthRates.map(({ growthRateId, growthRate, description }, i) => (
                      <TableCell key={i}>
                        <FormControlLabel
                          value={growthRateId}
                          control={<Radio disableRipple={true} />}
                          label={CardElement({
                            growthRateId,
                            growthRate,
                            description,
                            selectedGrowthRate,
                          })}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </RadioGroup>
        ) : (
          ''
        )}
        <div className="onboard-dimension">
          <div className="onboard-dimension-basis">{tc('DIMENSION_BASIS', { versionName })}</div>
          {budgetingDimensions && budgetingDimensions.length ? (
            <div className="onboard-dimension-list">
              {budgetingDimensions.map(item => (
                <div key={item.id} className="onboard-dimension-item">
                  <div className="onboard-dimension-icon">
                    {' '}
                    <i
                      className={`fa fa-${dimensionIcons[item.definitionType] ? dimensionIcons[item.definitionType] : dimensionIcons.Dimension}`}
                      aria-hidden="true"></i>
                  </div>
                  <div className="onboard-dimension-name" title={item.name}>
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      <ErrorMessage open={showErrorMessage} handleClose={closeErrorMessage} message={errorMessage} />
      <div className="align-center create-budget-btn-wrap">
        <button disabled={!selectedGrowthRate.length} color="primary" className={`btn btn-primary btn-next`} onClick={handleCreateBudget}>
          {tc('CREATE_BUDGET')}
        </button>
      </div>
    </div>
  );
};

export default Onboard;